import React, { useCallback, useEffect, useState } from 'react';
import { CgSpinner } from 'react-icons/cg';
import './OnCallAddNewCallForm.css';
import { userAppData } from '../MainApp.js';
import Select from 'react-select';
import BasicSingleLineTextEntry from '../common/formComponents/BasicSingleLineTextEntry.js';
import { SEC_ApiCall, SEC_DataApiPost } from '../func.js';
import LoadingComp from '../common/loading.js';
import { ApiError } from '../common/apiError.js';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const toastConfig = { autoClose: 4000, position: 'top-right' };


export function OncallAddNewCallForm(props) {
    const [submitButtonTextState, setSubmitButtonTextState] = useState('SUBMIT');
    const [canProxySubmitState, setCanProxySubmitState] = useState(false);

    // API CALL STATES
    const [isLoadedState, setIsLoadedState] = useState(false);
    const [apiErrorState, setApiErrorState] = useState(false);
    const [dataState, setDataState] = useState([]);
    // POST
    const [postApiInProgressState, setPostApiInProgressState] = useState(false);
    const [, setPostApiErrorState] = useState(false);

    // pupil name
    const [pupilNameState, setPupilNameState] = useState(null);
    // call type states
    const [callTypeState, setCallTypeState] = useState(null);
    // logging user states
    const [loggingUserState, setLoggingUserState] = useState({ 'id': userAppData['cogId']});

    // request location
    const [requestLocationState, setRequestLocationState] = useState('');
    const [requestLocationCharacterCountState, setRequestLocationCharacterCountState] = useState(0);
    const [requestLocationHasErrorState, setRequestLocationHasErrorState] = useState(false);
    const [requestLocationErrorTextState, setRequestLocationErrorTextState] = useState(false);
    // request description
    const [requestDescriptionState, setRequestDescriptionState] = useState('');
    const [requestDescriptionCharacterCountState, setRequestDescriptionCharacterCountState] = useState(0);
    const [requestDescriptionHasErrorState, setRequestDescriptionHasErrorState] = useState(false);
    const [requestDescriptionErrorTextState, setRequestDescriptionErrorTextState] = useState(false);

    const oncallSelectStyles = {
        control: (provided) => ({
            ...provided,
            boxShadow: 'none',
            border: '1px solid #c48e38',
            borderRadius: '6px',
            backgroundColor: '#ffffff',
            minHeight: '42px',
            '&:hover': {
                borderColor: '#c48e38'
            }
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '2px 10px'
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#706f6f'
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#706f6f'
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 5
        })
    };

    function getDefaultLoggingUser(loggableUsers) {
        const availableUsers = Array.isArray(loggableUsers) ? loggableUsers : [];
        const matchedCurrentUser = availableUsers.find((userOption) => userOption?.id === userAppData['cogId']);
        if (matchedCurrentUser) {
            return matchedCurrentUser;
        }
        return {
            id: userAppData['cogId'],
            displayName: userAppData['displayName'] || 'Current user',
        };
    }


    const apiCall = useCallback(async () => {
        var url = 'oncall/newCall';
        try {
            const response = await SEC_ApiCall(url, {}, setApiErrorState);
            setDataState(response);
            setLoggingUserState(getDefaultLoggingUser(response?.loggableUsers));

            if (userAppData["onCall"]['canProxySubmit']) {
                setCanProxySubmitState(true);
            }
            setIsLoadedState(true);
        } catch (e) {
            setApiErrorState(true);
            setIsLoadedState(true);
            console.log(e);
        }
    }, []);

    useEffect(() => {
        if (props.modalActiveState) {
            apiCall();
        }
    }, [apiCall, props.modalActiveState]);

    async function submitCallApiFunction(params, formData) {
        setPostApiInProgressState(true);
        const url = 'oncall';
        const submittedPupilName = pupilNameState?.pupilName || 'selected pupil';
        try {
            await SEC_DataApiPost(url, formData, params, setPostApiErrorState);

            toast.success(`✅ OnCall request successfully submitted for: ${submittedPupilName}`, toastConfig);
            setCallTypeState(null);
            setPupilNameState(null);
            if (canProxySubmitState) {
                setLoggingUserState(getDefaultLoggingUser(dataState?.loggableUsers));
            };
            setRequestLocationState('');
            setRequestDescriptionState('');
            setRequestLocationCharacterCountState(0);
            setRequestDescriptionCharacterCountState(0);
            setIsLoadedState(true);
            setSubmitButtonTextState('SUBMIT')
            setPostApiInProgressState(false);
            setPostApiErrorState(false);
            if (props.onSubmitSuccess) {
                props.onSubmitSuccess();
            }
        } catch (e) {
            setSubmitButtonTextState('SUBMIT')
            setPostApiInProgressState(false);
            setPostApiErrorState(true);
            toast.error('❌ Failed to submit the OnCall request. Please try again.', toastConfig);
            console.log(e);
        }
    };

    function submitNewOncallRequest() {
        let pupilNameIsBlank = false;
        let callTypeIsBlank = false;
        let requestLocationIsBlank = false;
        let requestDescriptionIsBlank = false;

        if (pupilNameState === null) {
            pupilNameIsBlank = true;
        }
        if (callTypeState === null) {
            callTypeIsBlank = true;
        }
        if (requestLocationState === '') {
            requestLocationIsBlank = true;
            setRequestLocationHasErrorState(true);
            setRequestLocationErrorTextState('Request location cannot be blank')
        }
        if (requestDescriptionState === '') {
            requestDescriptionIsBlank = true;
            setRequestDescriptionHasErrorState(true);
            setRequestDescriptionErrorTextState('Request description cannot be blank')
        }
        if (requestLocationIsBlank || requestDescriptionIsBlank || pupilNameIsBlank || callTypeIsBlank) {
            //error in a field, dont forget to check that a pupil is selected
            if (pupilNameIsBlank) {
                toast.error('❌ Please select a learner before submitting.', toastConfig);
            } else if (callTypeIsBlank) {
                toast.error('❌ Please select a call type before submitting.', toastConfig);
            } else {
                toast.error('❌ Please complete the required fields before submitting.', toastConfig);
            }
            console.log('error in field')
        } else {
            function isObjectValid(obj) {
                for (const key in obj) {
                    if (obj.hasOwnProperty(key) && obj[key] === undefined) {
                        // Handle the presence of an undefined value
                        // For example, alert or log
                        console.log(`The property ${key} is undefined.`);
                        return false; // Indicates the object is not valid for submission
                    }
                }
                return true; // All properties are defined
            }
            
            let formObj = {
                "callTypeId": callTypeState["id"],
                'pupil': pupilNameState['id'],
                'location': requestLocationState,
                'description': requestDescriptionState,
                'loggingUser': loggingUserState['id']
            }


            if (isObjectValid(formObj)) {
                // Proceed with submitting the object to the API
                setSubmitButtonTextState('SUBMITTING...')
                submitCallApiFunction(formObj, formObj);
            } else {
                // Handle the invalid object, e.g., prompt the user to fill all fields
                toast.error('❌ Failed to submit the OnCall request. Please try again.', toastConfig);
            }


        }
    }




    if (!isLoadedState) {
        return (
            <LoadingComp />
        )
    } else if (apiErrorState) {
        return (
            <ApiError />
        )
    } else {
        return (
            <div className='oncall_new_form'>
                <div className='oncall_new_header_wrap'>
                    <h1 className='oncall_new_header'>New OnCall Request</h1>
                    <p className='oncall_new_subtitle'>Log a new learner call using the current OnCall workflow.</p>
                </div>

                <div className='oncall_new_current_card'>
                    <span className='oncall_new_current_label'>Current On Call</span>
                    <strong className='oncall_new_current_value'>
                        {dataState?.currentOnCallDisplayName || 'No Person Found'}
                    </strong>
                </div>

                <div className='oncall_new_details_card'>
                    <p className='oncall_new_details_text'>Complete the learner, call type, location and reason fields before submitting.</p>
                </div>

                <div className='oncall_new_section_card'>
                    <div className='oncall_new_form_row'>
                    <div className='oncall_new_form_row_col1'>
                        <p className='oncall_new_form_row_text'>Learner:</p>
                        <Select
                            className='oncall_Pupil_Selector'
                            maxMenuHeight={'200px'}
                            value={pupilNameState}
                            getOptionLabel={option => option.pupilName}
                            getOptionValue={option => option.id}
                            options={dataState['pupils']}
                            styles={oncallSelectStyles}
                            placeholder='Select or start typing a learner'
                            noOptionsMessage={() => 'Unable to find learners'}
                            onChange={setPupilNameState}
                            isSearchable
                        />
                    </div>
                    <div className='oncall_new_form_row_col1'>
                        <p className='oncall_new_form_row_text'>Call Type:</p>
                        <Select
                            className='oncall_Pupil_Selector'
                            maxMenuHeight={'200px'}
                            value={callTypeState}
                            getOptionLabel={option => option.typeName}
                            getOptionValue={option => option.id}
                            options={dataState['callTypes']}
                            styles={oncallSelectStyles}
                            placeholder='Select a call type'
                            noOptionsMessage={() => 'Sorry something went wrong finding call types'}
                            onChange={setCallTypeState}
                            isSearchable
                        />
                    </div>
                </div>
                <div className='oncall_new_form_row'>
                    <div className='oncall_new_form_row_col2'>
                        <p className='oncall_new_form_row_text'>Location:</p>
                        <BasicSingleLineTextEntry
                            data={requestLocationState}
                            setData={setRequestLocationState}
                            nullAllowed={false}
                            displayCounter={true}
                            maxCharacters='40'
                            onError={requestLocationHasErrorState}
                            onErrorSet={setRequestLocationHasErrorState}
                            errorText={requestLocationErrorTextState}
                            errorTextSet={setRequestLocationErrorTextState}
                            characterCount={requestLocationCharacterCountState}
                            setCharacterCount={setRequestLocationCharacterCountState}
                        />
                    </div>
                    <div className='oncall_new_form_row_col2'>

                        <p className='oncall_new_form_row_text'>Reason:</p>
                        <BasicSingleLineTextEntry
                            data={requestDescriptionState}
                            setData={setRequestDescriptionState}
                            nullAllowed={false}
                            displayCounter={true}
                            maxCharacters='80'
                            onError={requestDescriptionHasErrorState}
                            onErrorSet={setRequestDescriptionHasErrorState}
                            errorText={requestDescriptionErrorTextState}
                            errorTextSet={setRequestDescriptionErrorTextState}
                            characterCount={requestDescriptionCharacterCountState}
                            setCharacterCount={setRequestDescriptionCharacterCountState}
                        />
                    </div>
                </div>
                </div>

                {
                    !canProxySubmitState ? null : (
                        <div className='oncall_new_section_card'>
                            <div className='oncall_new_form_row'>
                                <div className='oncall_new_form_row_col1'>
                                    <p className='oncall_new_form_row_text'>Logging Person:</p>
                                    <Select
                                        className='oncall_Pupil_Selector'
                                        maxMenuHeight={'200px'}
                                        value={loggingUserState}
                                        getOptionLabel={option => option.displayName}
                                        getOptionValue={option => option.id}
                                        options={dataState['loggableUsers']}
                                        styles={oncallSelectStyles}
                                        placeholder='Select the staff member logging the call'
                                        noOptionsMessage={() => 'Sorry something went wrong finding staff data'}
                                        onChange={setLoggingUserState}
                                        isSearchable
                                    />
                                </div>
                            </div>
                        </div>
                    )
                }

                <div className='oncall_new_form_row_submit'>
                    <button
                        type='button'
                        className='formSubmitButton oncall_new_submit'
                        onClick={() => !postApiInProgressState && submitNewOncallRequest(props.updateCallApiCall)}
                        disabled={postApiInProgressState}
                    >
                        {postApiInProgressState ? <CgSpinner className='submit-button-spinner' /> : submitButtonTextState}
                    </button>
                </div>
            </div >
        )
    }
};
